import { MutableRefObject, useReducer, useEffect } from 'react';
// 常量
import { IBaseEditVirtualTableContext } from '../context/editVirtualTableContext';
import {
  EditVirtualTableDispatchType,
  IEditVirtualTablePayload,
} from '../interface';
import {
  STATUS_CREATED,
  STATUS_CREATED_CODE,
  HISTORY_LIST_DATA,
} from '../../constants/constant';
import { dataSourceClassification } from '../../utils/dataUtils';
import { FormInstance } from 'antd/lib/form';
import { setRecordFormData } from '../utils';

export interface IDetailPageAction {
  type: EditVirtualTableDispatchType;
  payload: IEditVirtualTablePayload;
}

// Action
function EdiTableContextAction(
  state: IBaseEditVirtualTableContext,
  action: IDetailPageAction,
): IBaseEditVirtualTableContext {
  const oldDataSource = state.dataSource || [];
  const {
    form,
    loading,
    dataSource,
    updateDataSource,
    changeItem,
    rowKey,
    flagIndex = 0,
    addItem,
    isEmpty,
    sort,
    coverItem,
    appendItem,
    fieldNames,
  } = action.payload;
  switch (action.type) {
    case 'init':
      return {
        form,
        dataSource: dataSource?.map((item, index) => ({
          ...item,
          key: flagIndex + index,
          [HISTORY_LIST_DATA]:
            item[STATUS_CREATED_CODE] === STATUS_CREATED.ADD ? false : true,
          [STATUS_CREATED_CODE]:
            item[STATUS_CREATED_CODE] || STATUS_CREATED.DEFAULT,
        })),
      };
    case 'change':
      return {
        dataSource: oldDataSource.map(item => {
          if (item.key === rowKey) {
            return {
              ...item,
              ...changeItem,
              [STATUS_CREATED_CODE]:
                item[STATUS_CREATED_CODE] === STATUS_CREATED.ADD
                  ? STATUS_CREATED.ADD
                  : STATUS_CREATED.UPDATE,
            };
          }
          return item;
        }),
      };
    case 'changeLoading':
      return {
        ...state,
        loading,
      };
    case 'addRow':
      let key = flagIndex || 0;
      let oldDataSourceCopy = oldDataSource;
      if (isEmpty) {
        oldDataSourceCopy = [];
        oldDataSource.forEach(item => {
          if (item[STATUS_CREATED_CODE] !== STATUS_CREATED.ADD) {
            oldDataSourceCopy.push({
              ...item,
              [STATUS_CREATED_CODE]: STATUS_CREATED.DELETE,
            });
          }
        });
      }
      if (Array.isArray(addItem)) {
        return {
          ...state,
          dataSource: [
            ...oldDataSourceCopy,
            ...addItem.map((v, k) => ({
              [STATUS_CREATED_CODE]: STATUS_CREATED.ADD,
              ...v,
              key: key + k,
            })),
          ],
        };
      }
      return {
        ...state,
        dataSource: [
          ...oldDataSourceCopy,
          {
            [STATUS_CREATED_CODE]: STATUS_CREATED.ADD,
            ...addItem,
            key: key,
          },
        ],
      };
    /** 覆盖 */
    case 'cover':
      let coverKey = flagIndex || 0;
      let coverDataSource: any[] = [];
      oldDataSource.forEach(item => {
        if (item[STATUS_CREATED_CODE] !== STATUS_CREATED.ADD) {
          coverDataSource.push({
            ...item,
            [STATUS_CREATED_CODE]: STATUS_CREATED.DELETE,
          });
        }
      });
      coverItem?.forEach((item, index) => {
        coverDataSource.push({
          ...item,
          [STATUS_CREATED_CODE]: STATUS_CREATED.ADD,
          key: coverKey + index,
        });
      });
      return {
        ...state,
        dataSource: coverDataSource,
      };
    /** 追加 */
    case 'append':
      if (Array.isArray(appendItem) && appendItem.length) {
        let appendKey = flagIndex || 0;
        let { deleteDataSource, effectiveDataSource } = dataSourceClassification(oldDataSource);
        let appendDataSource: any[] = [];
        appendItem.forEach(item => {
          const findIndex = effectiveDataSource.findIndex(v => v.key === item.key);
          if (findIndex >= 0) {
            const oldStatus = effectiveDataSource[findIndex][STATUS_CREATED_CODE];
            const newAppendItem = {
              ...item,
              [STATUS_CREATED_CODE]: oldStatus === STATUS_CREATED.DEFAULT ? STATUS_CREATED.UPDATE : oldStatus,
            };
            effectiveDataSource[findIndex] = newAppendItem;
            state.form?.setFieldsValue(setRecordFormData(newAppendItem, fieldNames));
          } else {
            appendDataSource.push({
              ...item,
              key: appendKey,
              [STATUS_CREATED_CODE]: STATUS_CREATED.ADD,
            });
            appendKey = appendKey + 1;
          }
        });
        return {
          ...state,
          dataSource: [...deleteDataSource, ...effectiveDataSource, ...appendDataSource],
        };
      }
      return state;
    case 'changeDataSource':
      return {
        ...state,
        dataSource: updateDataSource,
      };
    case 'deleteRow':
      if (rowKey === undefined) return state;
      const deleteData = oldDataSource.find(v => v.key === rowKey);
      if (deleteData) {
        let newDataSource = oldDataSource.filter(v => v.key !== rowKey);
        if (sort) {
          newDataSource = newDataSource.map(item => {
            if (item.key > rowKey) {
              return {
                ...item,
                [STATUS_CREATED_CODE]:
                  item[STATUS_CREATED_CODE] === STATUS_CREATED.ADD
                    ? STATUS_CREATED.ADD
                    : STATUS_CREATED.UPDATE,
              };
            }
            return item;
          });
        }
        // 判断当前删除行是否为历史数据
        if (
          deleteData[HISTORY_LIST_DATA] &&
          deleteData[STATUS_CREATED_CODE] !== STATUS_CREATED.ADD
        ) {
          newDataSource.unshift({
            ...deleteData,
            [STATUS_CREATED_CODE]: STATUS_CREATED.DELETE,
          });
        }
        return {
          ...state,
          dataSource: newDataSource,
        };
      }
      return state;
    default:
      return state;
  }
}

// 初始数据
const initEditVirtualTableContext: IBaseEditVirtualTableContext = {};

// useEditVirtualTableContext
function useEditVirtualTableContext(
  flagIndex: MutableRefObject<number>,
  dataSource?: any[],
  form?: FormInstance<any>
) {
  const [EditVirtualTableContext, dispatch] = useReducer(
    EdiTableContextAction,
    initEditVirtualTableContext,
  );
  useEffect(() => {
    const num = flagIndex.current;
    flagIndex.current = num + (dataSource?.length || 0);
    dispatch({ type: 'init', payload: { dataSource, flagIndex: num, form, } });
  }, [dataSource]);
  function dispatchEditVirtualTable(
    type: EditVirtualTableDispatchType,
    payload: IEditVirtualTablePayload = {},
  ) {
    const { changeItem, rowKey, addItem, isEmpty, updateDataSource, loading, coverItem, appendItem, fieldNames, } = payload;
    const num = flagIndex.current;
    if (type === 'addRow') {
      flagIndex.current = Array.isArray(addItem)
      ? num + addItem.length
      : num + 1;
    }
    if (type === 'cover' && Array.isArray(coverItem)) {
      flagIndex.current = num + coverItem.length;
    }
    if (type === 'append' && Array.isArray(appendItem)) {
      flagIndex.current = num + appendItem.length;
    }
    switch (type) {
      case 'change':
        dispatch({ type, payload: { changeItem, rowKey } });
        break;
      case 'changeLoading':
        dispatch({ type, payload: { loading } });
        break;
      case 'cover':
        dispatch({ type, payload: { coverItem, flagIndex: num, } });
        break;
      case 'append':
        dispatch({ type, payload: { appendItem, fieldNames, flagIndex: num, } });
        break;
      case 'addRow':
        dispatch({ type, payload: { addItem, flagIndex: num, isEmpty } });
        break;
      case 'changeDataSource':
        dispatch({ type, payload: { updateDataSource } });
        break;
      case 'deleteRow':
        dispatch({ type, payload: { rowKey, } });
        break;
    }
  }
  return {
    ...EditVirtualTableContext,
    dispatchEditVirtualTable,
  };
}

export default useEditVirtualTableContext;
